import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

interface UsePoliceOverviewNavigationProps {
  matterId: string;
  setSelectedReportId: (id: string | null) => void;
}

export const usePoliceOverviewNavigation = ({
  matterId,
  setSelectedReportId,
}: UsePoliceOverviewNavigationProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleReportSelect = useCallback(
    (reportId: string) => {
      if (!reportId?.trim()) {
        toast.error('Invalid report ID');
        return;
      }

      if (!matterId?.trim()) {
        toast.error('Invalid matter ID');
        return;
      }

      try {
        setSelectedReportId(reportId);
        // Preserve the back parameter when navigating to report details
        const backParam = searchParams.get('back');
        const backQuery = backParam
          ? `?back=${encodeURIComponent(backParam)}`
          : '';
        navigate(
          `/matters/${encodeURIComponent(matterId.trim())}/police/${encodeURIComponent(reportId)}${backQuery}`,
        );
      } catch (err) {
        console.error('Navigation error:', err);
        toast.error('Failed to navigate to police report');
      }
    },
    [matterId, navigate, searchParams, setSelectedReportId],
  );

  const handleBackToList = useCallback(() => {
    if (!matterId?.trim()) {
      toast.error('Invalid matter ID');
      return;
    }

    try {
      setSelectedReportId(null);
      navigate(`/matters/${encodeURIComponent(matterId.trim())}/police`);
    } catch (err) {
      console.error('Navigation error:', err);
      toast.error('Failed to navigate back to list');
    }
  }, [matterId, navigate, setSelectedReportId]);

  const handleBackToCoverage = useCallback(() => {
    if (!matterId?.trim()) {
      toast.error('Invalid matter ID');
      return;
    }

    try {
      navigate(`/matters/${encodeURIComponent(matterId.trim())}/coverage`);
    } catch (err) {
      console.error('Navigation error:', err);
      toast.error('Failed to navigate to coverage');
    }
  }, [matterId, navigate]);

  const handleCreateReport = useCallback(() => {
    if (!matterId?.trim()) {
      toast.error('Invalid matter ID');
      return;
    }

    try {
      navigate(`/matters/${encodeURIComponent(matterId.trim())}/police/create`);
    } catch (err) {
      console.error('Navigation error:', err);
      toast.error('Failed to navigate to create report');
    }
  }, [matterId, navigate]);

  return {
    handleReportSelect,
    handleBackToList,
    handleBackToCoverage,
    handleCreateReport,
  };
};
